import { useEffect, useRef, useState } from "react";

const DIGIT_HEIGHT = 40; // matches CSS .rolling-digit-item height

interface RollingDigitProps {
  /** The target digit (0-9) or a character like '.' */
  digit: string;
  /** Delay before this digit starts animating (ms) */
  delay?: number;
  /** Total animation duration (ms) */
  duration?: number;
  /** Whether the animation should play */
  animate: boolean;
}

/**
 * A single rolling/slot-machine digit.
 * Scrolls vertically through 0-9 and lands on the target digit.
 * Non-numeric characters (like '.') are rendered statically.
 */
const RollingDigit = ({
  digit,
  delay = 0,
  duration = 1800,
  animate,
}: RollingDigitProps) => {
  const isNumeric = /^[0-9]$/.test(digit);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (!animate || !isNumeric) return;

    const delayTimer = setTimeout(() => {
      setHasStarted(true);
    }, delay);

    return () => clearTimeout(delayTimer);
  }, [animate, delay, isNumeric]);

  // For non-numeric characters (., M, etc.), just render them with a fade-in
  if (!isNumeric) {
    return (
      <span
        className="rolling-digit-static"
        style={{
          opacity: animate ? 1 : 0,
          transition: `opacity 0.4s ease ${delay}ms`,
        }}
      >
        {digit}
      </span>
    );
  }

  const targetNum = parseInt(digit, 10);

  // Build the strip: scroll through multiple full cycles (0-9) then land on target
  const totalCycles = 3;
  const digits: number[] = [];
  for (let cycle = 0; cycle < totalCycles; cycle++) {
    for (let d = 0; d <= 9; d++) {
      digits.push(d);
    }
  }
  // Add final run up to the target digit
  for (let d = 0; d <= targetNum; d++) {
    digits.push(d);
  }

  const targetIndex = digits.length - 1;
  const translatePx = targetIndex * DIGIT_HEIGHT;

  return (
    <span className="rolling-digit-container">
      <span
        className="rolling-digit-strip"
        style={{
          transform: hasStarted
            ? `translateY(-${translatePx}px)`
            : "translateY(0px)",
          transition: hasStarted
            ? `transform ${duration}ms cubic-bezier(0.15, 0.85, 0.35, 1)`
            : "none",
        }}
      >
        {digits.map((d, i) => (
          <span key={i} className="rolling-digit-item">
            {d}
          </span>
        ))}
      </span>
    </span>
  );
};

export default RollingDigit;

// ─── Slot Machine Number Display ────────────────────────────────

interface SlotMachineNumberProps {
  /** The formatted value string, e.g. "3.5", "13", "64" */
  value: string;
  /** Whether to animate (triggered by intersection) */
  animate: boolean;
  /** Base duration per digit in ms */
  duration?: number;
  /** Stagger delay between digits in ms */
  stagger?: number;
}

export const SlotMachineNumber = ({
  value,
  animate,
  duration = 1800,
  stagger = 150,
}: SlotMachineNumberProps) => {
  return (
    <span className="slot-machine-number">
      {value.split("").map((char, index) => (
        <RollingDigit
          key={`${index}-${char}`}
          digit={char}
          delay={index * stagger}
          duration={duration + index * 100}
          animate={animate}
        />
      ))}
    </span>
  );
};
